import { cn } from "@/lib/utils";

export type MobilePillTabItem<T extends string = string> = {
  value: T;
  label: React.ReactNode;
};

type MobilePillTabsProps<T extends string = string> = {
  items: MobilePillTabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
  /** Stretch pills to equal width (good for 2 tabs). Default: horizontal scroll. */
  equalWidth?: boolean;
};

/**
 * Top pill / chip tabs for mobile (web + native). Replaces legacy bottom tab bars.
 */
export function MobilePillTabs<T extends string>({
  items,
  value,
  onChange,
  className,
  equalWidth = false,
}: MobilePillTabsProps<T>) {
  if (equalWidth) {
    return (
      <div
        className={cn(
          "grid gap-2 mb-3",
          items.length === 2 && "grid-cols-2",
          items.length === 3 && "grid-cols-3",
          items.length >= 4 && "grid-cols-2",
          className,
        )}
        role="tablist"
      >
        {items.map((tab) => {
          const active = value === tab.value;
          return (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChange(tab.value)}
              className={cn(
                "native-scroll-chip rounded-full border px-3 text-[13px] font-medium transition-colors",
                active
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-background text-foreground",
              )}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "native-scroll-chips native-orders-tab-chips -mx-1 px-1 mb-3",
        className,
      )}
    >
      <div
        className="native-scroll-chips-track flex w-max max-w-full gap-1.5 mx-auto"
        role="tablist"
      >
        {items.map((tab) => {
          const active = value === tab.value;
          return (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChange(tab.value)}
              className={cn(
                "native-scroll-chip shrink-0 rounded-full border px-2.5 text-[12px] font-medium whitespace-nowrap transition-colors",
                active
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-background text-foreground",
              )}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
